// ViewLocationEntry.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { hasRoleAccess } from './utils/roles';
import DocumentUploader from './DocumentUploader';
import './ViewLocationEntry.css';

const ViewLocationEntry = ({ userRole }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [imageUrls, setImageUrls] = useState({}); // { [DocumentID]: dataUrl }
  const [isMobile, setIsMobile] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const canUploadDocs = hasRoleAccess(userRole, 'document-create');

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch entry data
  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const entryResponse = await axios.get(`${apiBaseUrl}/api/location-entry/${id}`);
        setEntry(entryResponse.data);
      } catch (error) {
        console.error('Error fetching entry:', error);
      } finally {
        // Mark loading as complete regardless of error for entry
        setLoading(false);
      }
    };

    fetchEntry();
  }, [id, apiBaseUrl]);

  // Fetch documents separately and handle 404 gracefully
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const documentsResponse = await axios.get(`${apiBaseUrl}/api/location-entry/${id}/documents`);
        setDocuments(documentsResponse.data);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // No documents found—set to empty array
          setDocuments([]);
        } else {
          console.error('Error fetching documents:', error);
        }
      }
    };

    fetchDocuments();
  }, [id, apiBaseUrl]);

  // Fetch image data for image files
  useEffect(() => {
    const isImageFile = (fileName) => {
      const ext = fileName.split('.').pop().toLowerCase();
      return ['png', 'jpg', 'jpeg', 'gif'].includes(ext);
    };

    const fetchImageData = async (doc) => {
      try {
        const blobResponse = await axios.get(`${apiBaseUrl}/api/download-item/${id}`, {
          params: { fileName: doc.FileName },
          responseType: 'blob',
        });
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageUrls((prev) => ({ ...prev, [doc.DocumentID]: reader.result }));
        };
        reader.readAsDataURL(blobResponse.data);
      } catch (error) {
        console.error('Error fetching image data for', doc.FileName, error);
      }
    };

    documents.forEach((doc) => {
      if (isImageFile(doc.FileName) && !imageUrls[doc.DocumentID]) {
        fetchImageData(doc);
      }
    });
  }, [documents, id, apiBaseUrl, imageUrls]);

  // Download file
  const handleDownload = async (fileName) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/download-item/${id}`, {
        params: { fileName },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // View PDF in new tab
  const handleViewPdf = async (fileName) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/download-item/${id}`, {
        params: { fileName },
        responseType: 'blob',
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
    }
  };

  // Back button handler: navigate to shipping-item view if possible
  const handleBack = () => {
    if (entry && entry.ShippingID) {
      navigate(`/shipping-item/${entry.ShippingID}`);
    } else {
      navigate(-1);
    }
  };

  // Display loading overlay while entry data is being fetched
  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!entry) {
    return <div className="view-location-container">Entry not found</div>;
  }

  return (
    <div className="view-location-container">
      <h1>View Location Entry</h1>

      <div className="location-details">
        <p>
          <strong>Entry ID:</strong> {entry.EntryID}
        </p>
        <p>
          <strong>Shipping ID:</strong> {entry.ShippingID}
        </p>
        <p>
          <strong>Location:</strong> {entry.Location}
        </p>
        <p>
          <strong>Date Placed:</strong> {entry.DatePlaced}
        </p>
        <p>
          <strong>Notes:</strong> {entry.Notes}
        </p>
        <p>
          <strong>Quantity:</strong> {entry.Qty}
        </p>
      </div>

      <div className="documents-section">
        <h2>All Documents</h2>
        {documents.length === 0 ? (
          <p>No documents found</p>
        ) : (
          <ul>
            {documents.map((doc) => (
              <li key={doc.DocumentID}>
                {/* Left part: image (if any), file name, and notes */}
                <div className="left-part">
                  {(() => {
                    const isImageFile = (fileName) => {
                      const ext = fileName.split('.').pop().toLowerCase();
                      return ['png', 'jpg', 'jpeg', 'gif'].includes(ext);
                    };
                    return isImageFile(doc.FileName) && imageUrls[doc.DocumentID] ? (
                      <img
                        src={imageUrls[doc.DocumentID]}
                        alt={doc.FileName}
                        className="document-image"
                        style={{ maxWidth: '150px', marginRight: '15px', cursor: 'pointer' }}
                        onClick={() => setEnlargedImage(imageUrls[doc.DocumentID])}
                      />
                    ) : null;
                  })()}

                  <div className="file-info">
                    <span className="file-name">{doc.FileName}</span>
                    <span className="document-notes">
                      {doc.EntryNotes || 'No Notes Attached'}
                    </span>
                  </div>
                </div>

                {/* Right part: buttons */}
                <div className="right-part">
                  {(() => {
                    const isImageFile = (fileName) => {
                      const ext = fileName.split('.').pop().toLowerCase();
                      return ['png', 'jpg', 'jpeg', 'gif'].includes(ext);
                    };
                    const isPdfFile = (fileName) => fileName.toLowerCase().endsWith('.pdf');
                    if (isImageFile(doc.FileName)) {
                      return (
                        !isMobile && (
                          <button
                            onClick={() => handleDownload(doc.FileName)}
                            className="download-button"
                          >
                            Download
                          </button>
                        )
                      );
                    } else if (isPdfFile(doc.FileName)) {
                      return isMobile ? (
                        <button
                          onClick={() => handleViewPdf(doc.FileName)}
                          className="download-button"
                        >
                          View
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleViewPdf(doc.FileName)}
                            className="download-button"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDownload(doc.FileName)}
                            className="download-button"
                          >
                            Download
                          </button>
                        </>
                      );
                    } else {
                      return (
                        <button
                          onClick={() => handleDownload(doc.FileName)}
                          className="download-button"
                        >
                          Download
                        </button>
                      );
                    }
                  })()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canUploadDocs && (
        <DocumentUploader
          entryId={id}
          userRole={userRole}
          isMobile={isMobile}
          refreshDocuments={async () => {
            try {
              const documentsResponse = await axios.get(`${apiBaseUrl}/api/location-entry/${id}/documents`);
              setDocuments(documentsResponse.data);
            } catch (error) {
              console.error('Error refreshing documents:', error);
            }
          }}
        />
      )}

      <div className="form-buttons">
        {hasRoleAccess(userRole, 'location-update') && (
          <button
            onClick={() => navigate(`/edit-location-entry/${id}`)}
            className="global-edit-button"
          >
            Edit
          </button>
        )}
        <button onClick={handleBack} className="global-back-button">
          Back
        </button>
      </div>

      {enlargedImage && (
        <div className="image-modal" onClick={() => setEnlargedImage(null)}>
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative' }}
          >
            <button
              onClick={() => setEnlargedImage(null)}
              className="modal-close-button"
            >
              X
            </button>
            <img src={enlargedImage} alt="Enlarged" style={{ maxWidth: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewLocationEntry;
